import { ScaffolderInputHeader } from '@internal/plugin-scaffolder-common-field-react';
import React from 'react';

import { Box, MenuItem, TextField } from '@material-ui/core';

import { DEPLOYMENT_TYPE_INFO } from '../infoDoc';
import { useStyles } from '../styles';
import {
  DEFAULT_HELM_CONFIGURATION,
  DEFAULT_SOURCE_CONFIGURATION,
  HelmConfiguration,
  SourceConfiguration,
} from '../types';
import { inferRepoType, resolveRepoType } from '../utils';
import { HelmValuesConfig } from './HelmValuesConfig';
import { SourceFields } from './SourceFields';

function normalizeSourceRepoFields(
  source: SourceConfiguration,
): SourceConfiguration {
  const next: SourceConfiguration = { ...source };
  const inferred = inferRepoType(next.repoURL);

  if (inferred !== null || next.deploymentType !== 'helm') {
    delete next.repoType;
  }

  const repoType = resolveRepoType(next);
  if (repoType === 'git') {
    delete next.chart;
  } else if (repoType === 'helm') {
    delete next.path;
  } else {
    delete next.path;
    delete next.chart;
  }

  return next;
}

interface SourcePickerProps {
  value: SourceConfiguration | null;
  onChange: (value: SourceConfiguration | null) => void;
  errors: string[];
  enableAppSetSupport?: boolean;
}

export const SourcePicker = (props: SourcePickerProps) => {
  const { value, onChange, errors, enableAppSetSupport = false } = props;
  const classes = useStyles();
  const data = value ?? DEFAULT_SOURCE_CONFIGURATION;

  const handleSourceChange = (next: SourceConfiguration | null) => {
    onChange(next ? normalizeSourceRepoFields(next) : next);
  };

  const handleDeploymentTypeChange = (
    next: SourceConfiguration['deploymentType'],
  ) => {
    const base: SourceConfiguration = {
      ...data,
      deploymentType: next,
    };
    if (next === 'helm') {
      base.helm = data.helm ?? { ...DEFAULT_HELM_CONFIGURATION };
    } else {
      delete base.helm;
      delete base.chart;
    }
    handleSourceChange(base);
  };

  const handleHelmChange = (helm: HelmConfiguration) => {
    handleSourceChange({ ...data, helm });
  };

  return (
    <Box>
      <Box className={classes.inputContainer}>
        <ScaffolderInputHeader
          title="Deployment Type"
          description="How ArgoCD interprets the application source."
          infoDoc={DEPLOYMENT_TYPE_INFO}
        />
      </Box>
      <Box className={classes.inputContainer}>
        <TextField
          select
          label="Deployment Type"
          variant="outlined"
          color="secondary"
          margin="none"
          size="small"
          fullWidth
          required
          value={data.deploymentType ?? ''}
          onChange={e =>
            handleDeploymentTypeChange(
              e.target.value as SourceConfiguration['deploymentType'],
            )
          }
          error={Boolean(errors?.length > 0) && !data.deploymentType}
          helperText={
            data.deploymentType
              ? undefined
              : 'Select a deployment type to continue.'
          }
        >
          <MenuItem value="manifests">Manifests</MenuItem>
          <MenuItem value="helm">Helm</MenuItem>
          <MenuItem value="kustomize">Kustomize</MenuItem>
        </TextField>
      </Box>

      {data.deploymentType && (
        <>
          <SourceFields
            source={data}
            onChange={handleSourceChange}
            enableAppSetSupport={enableAppSetSupport}
            errors={errors}
          />
          {data.deploymentType === 'helm' && (
            <HelmValuesConfig
              helm={data.helm ?? DEFAULT_HELM_CONFIGURATION}
              onChange={handleHelmChange}
              enableAppSetSupport={enableAppSetSupport}
              errors={errors}
            />
          )}
        </>
      )}
    </Box>
  );
};
